// Used in avenue-asset-dialog.component.html

// Angular
import { KeyValue } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { FileLikeObject, FileUploader } from "ng2-file-upload";

// Services/Types/Helpers
import { FormHelper } from "#core/helpers";
import { CookieService } from "#services/cookie.service";
import { LocalStorage } from "#services/local-storage.service";
// RXJS/Store
import { AssetId, SubjectId } from "@carnegie-learning/common-types";
import { AssetAssociation, Edition, Perform, PerformMedia, assetTypeIds } from "@carnegie-learning/cure-types";
import { AvenueService } from "@carnegie-learning/data-access-angular-passport";
import { Observable } from "rxjs";

@Component({
    selector: "avenue-asset-form",
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./avenue-asset-form.component.html",
    styleUrls: ["./avenue-asset-form.component.scss"],
    standalone: false
})
export class AvenueAssetFormComponent implements OnChanges {
    exists = false;
    assetId?: AssetId;
    showFileProgressBar = false;
    disableUploadBtn = true;
    uploadedFilename = "";
    uploadedExt = "";
    mediaUrl = "";
    mediaUrl$: Observable<string>;
    mediaType?: number;
    isIos = false;
    currentFile: File | FileLikeObject | null = null;
    extensionsImg: string[] = ["jpg", "jpeg", "png"];

    @Input() isDistributor = false;
    @Input() userGUID?: string;
    @Input() performAsset?: PerformMedia;
    @Input() subjectId?: SubjectId;
    @Input() assetAssociation?: Edition;
    @Input() activePrograms?: AssetAssociation[];

    @Output() create = new EventEmitter<Perform>();
    @Output() update = new EventEmitter<Perform>();
    @Output() cancel = new EventEmitter<any>();

    assetForm = this.fb.group({
        description: ["", []],
        displayName: ["", [Validators.required]],
        visibility: ["self", Validators.required],
        publishedBy: ["", Validators.required],
        audiences: [["TCH", "STU"], Validators.required],
        levelIds: [[], [Validators.required]],
        editionIds: [[], [Validators.required]],
        subjectId: ["", [Validators.required]],
        assetTypeId: [assetTypeIds.PERFORM, [Validators.required]],
        isResource: [false, Validators.required],
        isAssignable: [false, Validators.required],
        isDeleted: [false, Validators.required],
        audit: [{ createdBy: "" }, Validators.required],
        fileName: ["", []],
        fileExt: ["", []],
        performType: ["", [Validators.required]],
    });

    mimeTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "video/quicktime",
        "video/mp4",
        "video/webm",
    ];

    apiURL = (<any>window).apiHost + "/api/avenue/assets/upload-file";
    authToken = this.cookieService.get("CL_TOKEN2");
    header = { name: "authorization", value: this.authToken };
    uploader = new FileUploader({
        // From https://www.askingbox.com/info/mime-types-of-microsoft-office-file-formats
        // AND https://www.lifewire.com/file-extensions-and-mime-types-3469109
        allowedMimeType: this.mimeTypes,
        maxFileSize: 64 * 1024 * 1024, // 64 MB
        url: this.apiURL,
        authToken: this.authToken,
        autoUpload: false,
        method: "POST",
    });
    public hasBaseDropZoneOver = false;

    videoBuffer?: Blob;

    visibilityOpts: KeyValue<string, string>[] = [];

    constructor(
        private fb: UntypedFormBuilder,
        public localStorage: LocalStorage,
        private cdr: ChangeDetectorRef,
        private avenueTasksService: AvenueService,
        private formHelper: FormHelper,
        private cookieService: CookieService
    ) {
        this.isIos = typeof MediaRecorder === "undefined";
        this.uploader.onAfterAddingFile = (item) => {
            item.withCredentials = false;
            this.disableUploadBtn = true;
            if (item.file.name) {
                item.file.name = item.file.name.toLowerCase();
            }
            this.currentFile = item.file;
        };
        this.visibilityOpts = this.formHelper.getVisibilityOpts();

        this.uploader.onSuccessItem = (
            item: any,
            response: any,
            status: any,
            headers: any
        ) => {
            this.disableUploadBtn = true;
            const responseObj = JSON.parse(response);
            this.uploadedFilename = responseObj.fileName;
            this.uploadedExt = responseObj.extension;
            this.mediaUrl = responseObj.mediaUrl;

            this.mediaType =
                responseObj.extension === "jpg" ||
                responseObj.extension === "jpeg" ||
                responseObj.extension === "png"
                    ? 1
                    : 0;

            this.showFileProgressBar = false;

            this.clearSubmissionSet();

            this.cdr.detectChanges();
        };

        this.uploader.onErrorItem = (
            item: any,
            response: any,
            status: any,
            headers: any
        ) => {
            this.showFileProgressBar = false;

            this.cdr.detectChanges();
        };
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.performAsset && this.performAsset.assetId) {
            this.mediaType = this.performAsset.performType;
            this.exists = true;
            this.assetId = this.performAsset.assetId;

            this.assetForm.patchValue({
                publishedBy: this.performAsset.publishedBy,
                audiences: this.performAsset.audiences,
                description: this.performAsset.description,
                displayName: this.performAsset.displayName,
                visibility: this.performAsset.visibility,
                subjectId: this.performAsset.subjectId,
                scorePossible: this.performAsset.scorePossible,
                levelIds: this.performAsset.levelIds + "",
                editionIds: this.performAsset.editionIds,
                audit: this.performAsset.audit,
                assetTypeId: this.performAsset.assetTypeId,
                fileName: this.performAsset.fileName,
                fileExt: this.performAsset.fileExt,
                performType: this.performAsset.performType,
            });

            this.uploadedFilename = this.performAsset.fileName;
            this.uploadedExt = this.performAsset.fileExt;
            this.mediaUrl$ = this.avenueTasksService.getMediaUrl(
                this.performAsset
            );
        } else {
            this.assetForm.patchValue({
                publishedBy: this.userGUID,
                fileName: this.uploadedFilename,
                fileExt: this.uploadedExt,
                audit: { createdBy: this.userGUID },
                audiences: ["TCH", "STU"],
                editionIds: this.assetAssociation?.editionId
                    ? [this.assetAssociation.editionId]
                    : [],
                levelIds: this.assetAssociation?.levels
                    ? this.assetAssociation.levels.map((lvl) => lvl.levelId)
                    : [],
                subjectId: this.subjectId,
                assetTypeId: assetTypeIds.PERFORM,
                isResource: false,
                isAssignable: false,
                isDeleted: false,
                performType: this.performTypeReturn(this.uploadedExt),
            });
        }
    }

    createPerformAsset() {
        if (this.assetForm.valid) {
            this.assetForm.value.fileName = this.uploadedFilename;
            this.assetForm.value.fileExt = this.uploadedExt;
            this.assetForm.value.performType = this.performTypeReturn(
                this.uploadedExt
            );
            this.assetForm.value.levelIds = this.assetAssociation?.levels
                ? this.assetAssociation.levels.map((lvl) => lvl.levelId)
                : [];
            this.assetForm.value.publishedBy = this.userGUID;
            this.assetForm.value.audit = { createdBy: this.userGUID };
            this.assetForm.value.audiences = ["TCH", "STU"];
            this.assetForm.value.editionIds = this.assetAssociation?.editionId
                ? [this.assetAssociation.editionId]
                : [];
            this.assetForm.value.subjectId = this.subjectId;
            this.assetForm.value.assetTypeId = assetTypeIds.PERFORM;
            this.assetForm.value.isResource = false;
            this.assetForm.value.isAssignable = false;
            this.assetForm.value.isDeleted = false;
            this.create.emit(this.assetForm.value);
        }
    }

    updatePerformAsset() {
        if (this.assetForm.valid) {
            this.assetForm.value.assetId = this.assetId;
            this.assetForm.value.fileName = this.uploadedFilename;
            this.assetForm.value.fileExt = this.uploadedExt;
            this.assetForm.value.performType = this.performTypeReturn(
                this.uploadedExt
            );
            this.assetForm.value.levelIds = this.assetAssociation?.levels
                ? this.assetAssociation.levels.map((lvl) => lvl.levelId)
                : [];
            this.assetForm.value.publishedBy = this.userGUID;
            this.assetForm.value.audit = { createdBy: this.userGUID };
            this.assetForm.value.audiences = ["TCH", "STU"];
            this.assetForm.value.editionIds = this.assetAssociation?.editionId
                ? [this.assetAssociation.editionId]
                : [];
            this.assetForm.value.subjectId = this.subjectId;
            this.assetForm.value.assetTypeId = assetTypeIds.PERFORM;
            this.assetForm.value.isResource = false;
            this.assetForm.value.isAssignable = false;
            this.assetForm.value.isDeleted = false;
            this.update.emit(this.assetForm.value);
        }
    }

    cancelPerformAsset() {
        this.cancel.emit();
    }

    performTypeReturn(ext: string): number {
        return this.extensionsImg.includes(ext) ? 1 : 0;
    }

    // File Input
    fileSelected() {
        if (this.uploader.queue.length > 0) {
            // If the Queue is greater than 1, remove the first one.
            if (this.uploader.queue.length > 1) {
                this.uploader.queue[0].remove();
            }

            this.showFileProgressBar = false;
            this.disableUploadBtn = false;
        }
    }

    recordedFileEvent(event: Blob) {
        this.videoBuffer = event;

        this.disableUploadBtn = false;
        this.showFileProgressBar = false;
        this.cdr.detectChanges();
    }

    uploadFile() {
        if (this.currentFile) {
            // input uploaded
            this.uploader.addToQueue([this.currentFile as File]);
            this.uploader.queue[0].upload();
            this.showFileProgressBar = true;
        } else {
            if (this.videoBuffer) {
                // streamed
                const file = new File([this.videoBuffer], "recording.webm", {
                    type: "video/webm",
                });

                this.uploader.addToQueue([file]);
                this.uploader.queue[0].upload();
                this.showFileProgressBar = true;
            }
        }
    }

    disableUpdateMediaButton(): boolean {
        return (
            !this.assetForm.valid ||
            (this.assetForm.pristine && !this.mediaUrl) ||
            (!this.mediaUrl$ && !this.uploader.queue.length)
        );
    }

    clearSubmissionSet() {
        this.uploader.queue[0].remove();
        this.currentFile = null;
        this.disableUploadBtn = true;
    }

    public onUploaderChange(event: any): void {
        event.target.value = "";
    }

    public fileOverBase(e: any): void {
        this.hasBaseDropZoneOver = e;
    }

    getMimeTypes() {
        return this.mimeTypes.join(",");
    }
}
